import { Component, HostBinding, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OidcSecurityService, PublicEventsService, EventTypes } from 'angular-auth-oidc-client';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { SettingsService } from './core/settings/settings.service';
import { ThemeService, isForcedLightRoute } from './core/theme/theme.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    @HostBinding('class.layout-fixed') get isFixed() { return this.settings.getLayoutSetting('isFixed'); };
    @HostBinding('class.aside-collapsed') get isCollapsed() { return this.settings.getLayoutSetting('isCollapsed'); };
    @HostBinding('class.layout-boxed') get isBoxed() { return this.settings.getLayoutSetting('isBoxed'); };
    @HostBinding('class.layout-fs') get useFullLayout() { return this.settings.getLayoutSetting('useFullLayout'); };
    @HostBinding('class.hidden-footer') get hiddenFooter() { return this.settings.getLayoutSetting('hiddenFooter'); };
    @HostBinding('class.layout-h') get horizontal() { return this.settings.getLayoutSetting('horizontal'); };
    @HostBinding('class.aside-float') get isFloat() { return this.settings.getLayoutSetting('isFloat'); };
    @HostBinding('class.aside-toggled') get asideToggled() { return this.settings.getLayoutSetting('asideToggled'); };
    @HostBinding('class.aside-collapsed-text') get isCollapsedText() { return this.settings.getLayoutSetting('isCollapsedText'); };

    @HostBinding('class.nodesidebar-open') get nodeSidebarOpen() { return this.settings.getLayoutSetting('nodeSidebarOpen'); };

    constructor(
        public settings: SettingsService,
        private cdr: ChangeDetectorRef,
        private oidcSecurityService: OidcSecurityService,
        private eventService: PublicEventsService,
        private theme: ThemeService,
        private router: Router
    ) { }

    ngOnInit() {
        // Resolve light/dark before anything paints. Defaults to light, so this
        // is a no-op until someone explicitly opts in (see ThemeService).
        this.theme.initialize();

        // Pin pre-auth / registration / onboarding routes to light (CHAT-858).
        //
        // Seeded from the CURRENT url before subscribing, because NavigationEnd
        // does not fire for the navigation that is already in flight during
        // bootstrap -- and a deep link straight into /setup is precisely the
        // case this exists for.
        //
        // Kept in sync on every navigation rather than only on entry, so
        // LEAVING one of these routes releases the pin and restores whatever
        // the user's real preference was.
        this.theme.setForcedLight(isForcedLightRoute(this.router.url));
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe((e) => {
            this.theme.setForcedLight(isForcedLightRoute(e.urlAfterRedirects || e.url));
        });

        // Redirect to login when silent renewal fails (e.g. expired refresh token)
        this.eventService.registerForEvents().pipe(
            filter(event => event.type === EventTypes.SilentRenewFailed),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.oidcSecurityService.authorize();
        });

        // prevent empty links to reload the page
        document.addEventListener('click', e => {
            const target = e.target as HTMLElement;
            if (target.tagName === 'A' && ['', '#'].indexOf(target.getAttribute('href')) > -1)
                e.preventDefault();
        });

        // Subscribe to layout settings changes to trigger change detection
        this.settings.layoutSettingsChange.subscribe((change: any) => {
            console.log('Layout setting changed:', change);
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
